import { useState } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  addFileAction,
  addGroupFilesAction,
  addPrivateFileAction,
} from "store/actions/files";
import { useFetchFiles, useCurrentUser } from "utils/hooks";

interface onFileUploadProps {
  file?: File | null;
  args: any;
  onSuccess: (f: FileResponse["fileData"] | string) => void;
  allData?: boolean;
}
export interface FileResponse {
  fileData: {
    uuid: string;
    fileUrl: string;
    added_by: string;
    title: string;
    description: string;
    updatedAt: string;
    createdAt: string;
    groupId: string;
    practiceId: string;
    isPrivate: boolean;
    privatePatientId: string | null;
  };
}
const useFileUploader = () => {
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [message, setMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { user } = useCurrentUser();
  const { fetchTherapistFiles, fetchGroupFiles, fetchPrivateFiles } =
    useFetchFiles();

  const onFileChange = (files: null | File[]) => {
    const file = files && files[0];
    setSelectedFile(file);
    setMessage("");
  };

  const onFileUpload = async ({
    file,
    args = {
      isImage: false,
    },
    onSuccess,
    allData,
  }: onFileUploadProps) => {
    setIsLoading(true);
    const uploadFile = file || selectedFile;

    if (uploadFile) {
      if (uploadFile.size > 2 * 1024 * 1024 && !args.isImage) {
        setMessage("File size exceeds 2MB limit");
        toast("File size exceeds 2MB limit");
        setIsLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("args", JSON.stringify(args));

      try {
        const response = await fetch(
          `${process.env.REACT_APP_REST_API_URL}/uploader/upload`,
          {
            method: "POST",
            body: formData,
            credentials: "include",
            headers: {
              organizationId: user?.activeOrganizationId || "NA",
              permissionKey: "upload_files",
            },
          }
        );

        if (response.ok) {
          setMessage("File uploaded successfully");
          toast("File Added");
          const resData: FileResponse = await response.json();
          dispatch(addFileAction(resData.fileData));
          dispatch(addPrivateFileAction(resData.fileData));

          if (allData) {
            onSuccess && onSuccess(resData.fileData);
          } else {
            onSuccess && onSuccess(resData.fileData.uuid);
          }

          fetchTherapistFiles();
          fetchPrivateFiles();
        } else {
          const errorData = await response.json();
          if (errorData.error.includes("FILE_LIMIT_REACHED")) {
            toast.warn(`File limit reached, please upgrade`);
            setIsLoading(false);
          } else {
            setMessage(`Upload failed: ${errorData.message}`);
          }
        }
      } catch (error) {
        console.error("Error:", error);
        setMessage(`Error: ${error}`);
      }

      setIsLoading(false);
    } else {
      setMessage("No file selected");
      setIsLoading(false);
    }
  };

  const onGroupFileUpload = async ({
    file,
    args = {
      isImage: false,
    },
    onSuccess,
    allData,
  }: onFileUploadProps) => {
    setIsLoading(true);
    const uploadFile = file || selectedFile;

    if (uploadFile) {
      if (uploadFile.size > 2 * 1024 * 1024 && !args.isImage) {
        setMessage("File size exceeds 2MB limit");
        toast("File size exceeds 2MB limit");
        setIsLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("args", JSON.stringify(args));

      try {
        const response = await fetch(
          `${process.env.REACT_APP_REST_API_URL}/uploader/upload`,
          {
            method: "POST",
            body: formData,
            credentials: "include",
            headers: {
              organizationId: user?.activeOrganizationId || "NA",
              permissionKey: "upload_files",
            },
          }
        );

        if (response.ok) {
          setMessage("File uploaded successfully");
          toast("File Added");
          const resData: FileResponse = await response.json();
          dispatch(addGroupFilesAction([resData.fileData]));

          if (allData) {
            onSuccess && onSuccess(resData.fileData);
          } else {
            onSuccess && onSuccess(resData.fileData.uuid);
          }

          fetchGroupFiles(args.groupId);
        } else {
          const errorData = await response.json();
          if (errorData.error.includes("FILE_LIMIT_REACHED")) {
            toast.warn(`File limit reached, please upgrade`);
            setIsLoading(false);
          } else {
            setMessage(`Upload failed: ${errorData.message}`);
          }
        }
      } catch (error) {
        console.error("Error:", error);
        setMessage(`Error: ${error}`);
      }

      setIsLoading(false);
    } else {
      setMessage("No file selected");
      setIsLoading(false);
    }
  };

  return {
    onFileChange,
    onFileUpload,
    onGroupFileUpload,
    selectedFile,
    message,
    isLoading,
  };
};

export default useFileUploader;
